/**
 * SnowFlow Multiplayer — player sync, chat, orbs, tag game, race, snowball, minimap.
 */
import { io }               from 'socket.io-client';
import { MeshBuilder }      from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3, Vector3, Matrix } from '@babylonjs/core/Maths/math';
import { GlowLayer }        from '@babylonjs/core/Layers/glowLayer';
import { input }            from '../core/input.js';

// ── Colour helpers ────────────────────────────────────────────────────────
const PALETTE = [
    new Color3(0.0,1.0,0.53), new Color3(0.55,0.78,1.0),
    new Color3(1.0,0.85,0.3), new Color3(0.85,0.4,1.0),
    new Color3(1.0,0.25,0.55),new Color3(0.1,0.9,1.0),
    new Color3(1.0,0.55,0.1), new Color3(0.35,1.0,0.25),
];
function colorOf(id) {
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (Math.imul(h,31) + id.charCodeAt(i)) >>> 0;
    return PALETTE[h % PALETTE.length];
}
function cssOf(c) { return `rgb(${Math.round(c.r*255)},${Math.round(c.g*255)},${Math.round(c.b*255)})`; }
function esc(s) { return s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;'); }

const TAG_COLOR  = new Color3(1.0, 0.18, 0.0);
const ORB_COLLECT_R2 = 12.25; // 3.5m

// Minimap world extents (±80 units shown)
const MAP_EXTENT = 80;
const PORTALS_MAP = [
    { x:0,  z:-28, label:'🏔️' }, { x:28, z:-8,  label:'🤖' },
    { x:-28,z:-8,  label:'🛍️' }, { x:0,  z:28,  label:'🎮' },
];

export class MultiplayerSystem {
    constructor(scene, engine) {
        this._scene   = scene;
        this._engine  = engine;
        this._socket  = null;
        this._selfId  = null;
        this._selfPos = { x:0, y:0, z:0 };
        this._remote  = {};
        this._orbs    = {};
        this._myScore = 0;
        this._taggedId = null;
        this._frozen  = false;
        this._frozenUntil = 0;
        this._racePortal = null;
        this._lastSend = 0;

        // DOM
        this._labelsEl  = document.getElementById('mp-labels');
        this._countEl   = document.getElementById('mp-count');
        this._scoreEl   = document.getElementById('mp-score');
        this._chatLog   = document.getElementById('chat-log');
        this._tagBig    = document.getElementById('tag-big-txt');
        this._tagSub    = document.getElementById('tag-sub-txt');
        this._tagBanner = document.getElementById('tag-banner');
        this._joinBanner= document.getElementById('join-banner');
        this._joinBig   = document.getElementById('join-big');
        this._joinSub   = document.getElementById('join-sub');
        this._mmCanvas  = document.getElementById('minimap-canvas');
        this._mmCtx     = this._mmCanvas?.getContext('2d');
        this._affiliates= []; // set by main.js after AffiliateSystem init

        this._glow = new GlowLayer('mpGlow', scene, { blurKernelSize: 32 });
        this._glow.intensity = 1.5;

        this._connect();

        // Snowball key — Space
        window.addEventListener('keydown', (e) => {
            if (e.code === 'Space' && !e.repeat && this._socket?.connected) {
                e.preventDefault();
                this._socket.emit('snowball');
                this._showSnowballFx();
            }
        });

        // Mobile snowball button
        document.getElementById('mobile-snowball')?.addEventListener('touchstart', (e) => {
            e.stopPropagation();
            this._socket?.emit('snowball');
            this._showSnowballFx();
        }, { passive: false });
    }

    // ── Connection ────────────────────────────────────────────────────────
    _connect() {
        this._socket = io('https://liortesta.com', {
            path: '/snowflow-ws/socket.io',
            transports: ['websocket','polling'],
            reconnectionDelay: 2000,
        });

        this._socket.on('init', ({ self, players, orbs, taggedId, race }) => {
            this._selfId   = self.id;
            this._taggedId = taggedId;
            for (const p of players)          this._spawnPlayer(p);
            for (const o of (orbs || []))     this._spawnOrb(o);
            if (race) this._racePortal = race;
            this._refreshCount();
        });

        this._socket.on('player_join',  p     => {
            this._spawnPlayer(p);
            this._refreshCount();
            this._showJoinBanner(p.name);
        });
        this._socket.on('player_leave', ({id})=> { this._despawnPlayer(id); this._refreshCount(); });
        this._socket.on('player_count', n     => { this._setCount(n); });
        this._socket.on('player_move',  d     => {
            const r = this._remote[d.id];
            if (r) { r.target.x=d.x; r.target.y=d.y; r.target.z=d.z; }
        });

        this._socket.on('chat', ({ id, name, color, msg }) => {
            const isSelf = id === this._selfId;
            const kind   = id === '__sys' ? 'system' : isSelf ? 'self' : 'other';
            this._showChat(name, color, msg, kind);
        });

        this._socket.on('orb_spawn',     o          => this._spawnOrb(o));
        this._socket.on('orb_collected', ({orbId, playerId, score}) => {
            this._despawnOrb(orbId);
            if (playerId === this._selfId) { this._myScore = score; this._flashScore(); }
            const r = this._remote[playerId];
            if (r) { r.score = score; this._updateLabel(r); }
        });

        this._socket.on('tag_update', ({ taggedId, name }) => {
            this._taggedId = taggedId;
            this._applyTagColors();
            const isMe = taggedId === this._selfId;
            this._showTagBanner(
                isMe ? '👿 אתה "זה"!' : `🏃 ברח!`,
                isMe ? 'תגע במישהו כדי לעביר!' : `${name} רודף אחריך`,
            );
        });

        this._socket.on('race_start', portal => {
            this._racePortal = portal;
            this._showTagBanner(`🏁 מירוץ!`, `רוץ לפורטל "${portal.label}"!`);
        });
        this._socket.on('race_end', ({ winnerId, winnerName } = {}) => {
            this._racePortal = null;
            if (winnerId) this._showTagBanner(`🥇 ${winnerName} ניצח!`, '');
        });

        this._socket.on('hit_by_snowball', ({ fromName }) => {
            this._frozen     = true;
            this._frozenUntil = performance.now() + 3000;
            this._showTagBanner(`❄️ הוקפאת!`, `${fromName} פגע בך! 3 שניות...`);
        });

        this._socket.on('disconnect', () => {
            for (const id of Object.keys(this._remote)) this._despawnPlayer(id);
            for (const id of Object.keys(this._orbs))   this._despawnOrb(id);
            this._refreshCount();
        });
    }

    // ── Player avatars ────────────────────────────────────────────────────
    _spawnPlayer(p) {
        if (this._remote[p.id]) return;
        const scene = this._scene;
        const color = colorOf(p.id);

        const body = MeshBuilder.CreateBox(`mpB_${p.id}`, { width:.58, height:1.15, depth:.36 }, scene);
        body.renderingGroupId = 1;
        const head = MeshBuilder.CreateSphere(`mpH_${p.id}`, { diameter:.52, segments:8 }, scene);
        head.renderingGroupId = 1;

        const mat = new StandardMaterial(`mpM_${p.id}`, scene);
        mat.emissiveColor  = color.clone();
        mat.diffuseColor   = color.scale(0.55);
        mat.specularPower  = 32;
        body.material = head.material = mat;

        this._glow.addIncludedOnlyMesh(body);
        this._glow.addIncludedOnlyMesh(head);

        const label = document.createElement('div');
        label.className = 'mp-label';
        label.style.color = cssOf(color);
        label.style.display = 'none';
        this._labelsEl.appendChild(label);

        this._remote[p.id] = {
            body, head, mat, label, color,
            pos:    { x:p.x||0, y:p.y||0, z:p.z||0 },
            target: { x:p.x||0, y:p.y||0, z:p.z||0 },
            name: p.name, score: p.score||0, isTagged: false,
        };
        this._updateLabel(this._remote[p.id]);
        if (this._taggedId === p.id) this._applyTagColors();
    }

    _despawnPlayer(id) {
        const r = this._remote[id];
        if (!r) return;
        r.body.dispose(); r.head.dispose(); r.mat.dispose(); r.label.remove();
        delete this._remote[id];
    }

    _updateLabel(r) {
        const tag   = r.isTagged ? ' 👿' : '';
        const score = r.score > 0 ? `  ⭐${r.score}` : '';
        r.label.textContent = r.name + tag + score;
    }

    _applyTagColors() {
        for (const [id, r] of Object.entries(this._remote)) {
            const isIt = id === this._taggedId;
            r.isTagged = isIt;
            r.mat.emissiveColor = isIt ? TAG_COLOR.clone() : r.color.clone();
            this._updateLabel(r);
        }
    }

    // ── Orbs ──────────────────────────────────────────────────────────────
    _spawnOrb(orb) {
        if (this._orbs[orb.id]) return;
        const scene = this._scene;

        const mesh = MeshBuilder.CreateSphere(`orb_${orb.id}`, { diameter:.72, segments:10 }, scene);
        mesh.position.set(orb.x, 0.6, orb.z);
        mesh.renderingGroupId = 1;

        const mat = new StandardMaterial(`orbM_${orb.id}`, scene);
        mat.emissiveColor = new Color3(1,.88,.2);
        mat.disableLighting = true;
        mesh.material = mat;

        const ring = MeshBuilder.CreateTorus(`orbR_${orb.id}`, { diameter:1.1, thickness:.055, tessellation:28 }, scene);
        ring.position.set(orb.x, 0.6, orb.z);
        ring.rotation.x = Math.PI/2;
        ring.renderingGroupId = 1;
        const rMat = new StandardMaterial(`orbRM_${orb.id}`, scene);
        rMat.emissiveColor = new Color3(1,.7,.1);
        rMat.disableLighting = true;
        ring.material = rMat;

        this._glow.addIncludedOnlyMesh(mesh);
        this._glow.addIncludedOnlyMesh(ring);

        this._orbs[orb.id] = { id:orb.id, x:orb.x, z:orb.z, mesh, ring, mat, rMat };
    }

    _despawnOrb(id) {
        const o = this._orbs[id];
        if (!o) return;
        o.mesh.dispose(); o.ring.dispose(); o.mat.dispose(); o.rMat.dispose();
        delete this._orbs[id];
    }

    // ── Chat ─────────────────────────────────────────────────────────────
    sendChat(text) { this._socket?.emit('chat', text); }

    _showChat(name, color, msg, kind) {
        if (!this._chatLog) return;
        const el = document.createElement('div');
        el.className = `chat-msg chat-${kind}`;

        const nameSpan = document.createElement('span');
        nameSpan.className = 'chat-name';
        nameSpan.textContent = name;
        if (color && kind !== 'system') nameSpan.style.color = color;

        const textNode = document.createTextNode(' ' + msg);
        el.appendChild(nameSpan);
        el.appendChild(textNode);

        this._chatLog.appendChild(el);
        this._chatLog.scrollTop = this._chatLog.scrollHeight;
        while (this._chatLog.children.length > 40) this._chatLog.firstChild.remove();

        setTimeout(() => el.classList.add('chat-fade'), 12000);
        setTimeout(() => el.remove(), 13500);
    }

    // ── Tag / Race banners ────────────────────────────────────────────────
    _showTagBanner(big, sub) {
        if (!this._tagBanner) return;
        this._tagBig.textContent = big;
        this._tagSub.textContent = sub;
        this._tagBanner.classList.add('show');
        clearTimeout(this._bannerTimer);
        this._bannerTimer = setTimeout(() => this._tagBanner.classList.remove('show'), 3500);
    }

    _showSnowballFx() {
        this._showTagBanner('🌨️ כדור שלג!', 'קרוב לפגוע!');
    }

    _showJoinBanner(name) {
        if (!this._joinBanner) return;
        this._joinBig.textContent = `🆕 ${name} נכנס!`;
        this._joinBanner.classList.add('show');
        clearTimeout(this._joinTimer);
        this._joinTimer = setTimeout(() => this._joinBanner.classList.remove('show'), 3000);
    }

    // ── Score pill ────────────────────────────────────────────────────────
    _refreshCount() { this._setCount(Object.keys(this._remote).length + 1); }
    _setCount(n)    { if (this._countEl) this._countEl.textContent = `👥 ${n}`; }
    _flashScore()   {
        if (!this._scoreEl) return;
        this._scoreEl.textContent = `⭐ ${this._myScore}`;
        this._scoreEl.classList.add('score-pop');
        setTimeout(() => this._scoreEl.classList.remove('score-pop'), 600);
    }

    // ── Minimap ───────────────────────────────────────────────────────────
    _drawMinimap(charPos) {
        const ctx = this._mmCtx;
        if (!ctx) return;
        // Sync canvas resolution to CSS display size
        const displaySz = this._mmCanvas.parentElement.offsetWidth || 140;
        if (this._mmCanvas.width !== displaySz) {
            this._mmCanvas.width  = displaySz;
            this._mmCanvas.height = displaySz;
        }
        const sz  = this._mmCanvas.width;
        const cx  = sz / 2, cy = sz / 2;
        const scale = cx / MAP_EXTENT;

        ctx.clearRect(0, 0, sz, sz);

        // Circular clip
        ctx.save();
        ctx.beginPath(); ctx.arc(cx, cy, cx - 1, 0, Math.PI*2); ctx.clip();

        // Background
        ctx.fillStyle = 'rgba(4,10,20,0.85)';
        ctx.fillRect(0, 0, sz, sz);

        // Grid
        ctx.strokeStyle = 'rgba(143,196,232,0.08)';
        ctx.lineWidth = 0.5;
        for (let g = -80; g <= 80; g += 20) {
            const px = cx + g * scale, py = cy + g * scale;
            ctx.beginPath(); ctx.moveTo(px, 0); ctx.lineTo(px, sz); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(0, py); ctx.lineTo(sz, py); ctx.stroke();
        }

        // Portals
        ctx.font = `${sz < 100 ? 8 : 11}px serif`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        for (const p of PORTALS_MAP) {
            const mx = cx + p.x * scale, my = cy + p.z * scale;
            ctx.globalAlpha = 0.7;
            ctx.fillText(p.label, mx, my);
            ctx.globalAlpha = 1;
        }

        // Affiliate beacons (emoji labels)
        ctx.font = `${sz < 100 ? 7 : 9}px serif`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        for (const a of this._affiliates) {
            const mx = cx + a.x * scale, my = cy + a.z * scale;
            ctx.globalAlpha = 0.85;
            ctx.fillText(a.emoji, mx, my);
            ctx.globalAlpha = 1;
        }

        // Orbs (gold dots)
        for (const o of Object.values(this._orbs)) {
            const mx = cx + o.x * scale, my = cy + o.z * scale;
            ctx.fillStyle = '#ffd166';
            ctx.beginPath(); ctx.arc(mx, my, sz < 100 ? 2 : 3, 0, Math.PI*2); ctx.fill();
        }

        // Race portal (blinking marker)
        if (this._racePortal) {
            const blink = Math.floor(performance.now() / 350) % 2 === 0;
            if (blink) {
                const mx = cx + this._racePortal.x * scale, my = cy + this._racePortal.z * scale;
                ctx.strokeStyle = '#ff4'; ctx.lineWidth = 2;
                ctx.beginPath(); ctx.arc(mx, my, 8 * scale, 0, Math.PI*2); ctx.stroke();
            }
        }

        // Remote players (colored dots + initials)
        for (const r of Object.values(this._remote)) {
            const mx = cx + r.pos.x * scale, my = cy + r.pos.z * scale;
            ctx.fillStyle = r.isTagged ? '#ff2f00' : cssOf(r.color);
            ctx.beginPath(); ctx.arc(mx, my, sz < 100 ? 3 : 4, 0, Math.PI*2); ctx.fill();
        }

        // Self (white + arrow)
        const sx = cx + charPos.x * scale, sy = cy + charPos.z * scale;
        ctx.fillStyle = '#ffffff';
        ctx.beginPath(); ctx.arc(sx, sy, sz < 100 ? 4 : 5, 0, Math.PI*2); ctx.fill();
        ctx.strokeStyle = '#8fc4e8'; ctx.lineWidth = 1.5;
        ctx.stroke();

        ctx.restore();

        // Border ring
        ctx.strokeStyle = 'rgba(143,196,232,0.25)'; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.arc(cx, cy, cx - 1, 0, Math.PI*2); ctx.stroke();
    }

    // ── Per-frame update ──────────────────────────────────────────────────
    update(charPos, time, dt) {
        this._selfPos = charPos;
        const now = performance.now();

        // Freeze effect — zero out movement while frozen
        if (this._frozen) {
            if (now > this._frozenUntil) {
                this._frozen = false;
            } else {
                input.moveX = 0;
                input.moveZ = 0;
                input.moving = false;
            }
        }

        // Send position ~20fps
        if (now - this._lastSend > 50 && this._socket?.connected) {
            this._lastSend = now;
            this._socket.emit('move', { x:charPos.x, y:charPos.y, z:charPos.z, rotY:0 });
        }

        // Animate orbs + collect
        for (const [id, o] of Object.entries(this._orbs)) {
            const t = time * 2.3 + o.x * 0.4;
            o.mesh.position.y = 0.5 + Math.sin(t) * 0.18;
            o.ring.position.y = 0.5 + Math.sin(t) * 0.18;
            o.ring.rotation.y = time * 0.8;
            const p = 0.7 + 0.3 * Math.sin(time * 3.1 + o.z);
            o.mat.emissiveColor.set(p, p * 0.88, 0.2 * p);
            const dx = charPos.x - o.x, dz = charPos.z - o.z;
            if (dx*dx + dz*dz < ORB_COLLECT_R2 && this._socket?.connected) {
                this._socket.emit('collect_orb', Number(id));
            }
        }

        // Minimap
        this._drawMinimap(charPos);

        if (Object.keys(this._remote).length === 0) return;

        // Project labels
        const cw  = this._engine.getRenderingCanvas().clientWidth;
        const ch  = this._engine.getRenderingCanvas().clientHeight;
        const vp  = { x:0, y:0, width:cw, height:ch };
        const wm  = Matrix.Identity();
        const tm  = this._scene.getTransformMatrix();

        for (const r of Object.values(this._remote)) {
            const k = Math.min(1, dt * 10);
            r.pos.x += (r.target.x - r.pos.x) * k;
            r.pos.y += (r.target.y - r.pos.y) * k;
            r.pos.z += (r.target.z - r.pos.z) * k;

            r.body.position.set(r.pos.x, r.pos.y + 0.575, r.pos.z);
            r.head.position.set(r.pos.x, r.pos.y + 1.35,  r.pos.z);

            const wp = new Vector3(r.pos.x, r.pos.y + 2.3, r.pos.z);
            const sp = Vector3.Project(wp, wm, tm, vp);

            if (sp.z > 0 && sp.z < 1) {
                r.label.style.display = '';
                r.label.style.left    = `${sp.x}px`;
                r.label.style.top     = `${sp.y}px`;
            } else {
                r.label.style.display = 'none';
            }
        }
    }

    isFrozen() { return this._frozen && performance.now() < this._frozenUntil; }

    setAffiliates(beacons) { this._affiliates = beacons; }
}
